import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Check, X, Ban, Send, ArrowLeft } from 'lucide-react';
import { Button } from '../components';
import { B2bAgenda } from '../components/B2bAgenda';
import { B2bAfspraakDetail, B2bAfspraakFormulier } from '../components/B2bAgendaPanelen';
import {
  bevestigB2bAlsProfessional,
  dienB2bUrenIn,
  getB2bWerkgevers,
  getProfessionalB2bAfspraken,
  planB2bAfspraakAlsProfessional,
  trekB2bIn,
  weigerB2bAlsProfessional,
  type ProfessionalB2bAfspraak,
} from '../services/lopendeTrajectenApi';

/**
 * Professional-kant van de B2B-agenda: afspraken van opdrachtgevers accepteren,
 * zelf inplannen (opdrachtgever moet bevestigen) en na afloop uren indienen.
 */
const QK = ['professional-b2b'];
const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis');

const ProfessionalB2bAgendaPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: afspraken = [], isLoading } = useQuery({ queryKey: QK, queryFn: getProfessionalB2bAfspraken });
  const { data: werkgevers = [] } = useQuery({ queryKey: ['professional-b2b-werkgevers'], queryFn: getB2bWerkgevers });
  const [nieuwOp, setNieuwOp] = useState<Date | null>(null);
  const [geselecteerdId, setGeselecteerdId] = useState<string | null>(null);

  const actie = (fn: (id: string) => Promise<unknown>, melding: string) => ({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(melding);
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: foutmelding,
  });
  const plan = useMutation({
    mutationFn: planB2bAfspraakAlsProfessional,
    onSuccess: () => {
      toast.success('Afspraak ingepland — wacht op bevestiging door de opdrachtgever');
      setNieuwOp(null);
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: foutmelding,
  });
  const bevestig = useMutation(actie(bevestigB2bAlsProfessional, 'Afspraak geaccepteerd'));
  const weiger = useMutation(actie((id: string) => weigerB2bAlsProfessional(id, window.prompt('Reden van weigering (optioneel):') || undefined), 'Afspraak geweigerd'));
  const trekIn = useMutation(actie(trekB2bIn, 'Afspraak ingetrokken'));
  const dienIn = useMutation({
    mutationFn: ({ id, uren }: { id: string; uren: number }) => dienB2bUrenIn(id, uren),
    onSuccess: () => {
      toast.success('Uren ingediend — de opdrachtgever fiatteert ze');
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: foutmelding,
  });

  const geselecteerd = afspraken.find((r) => r.id === geselecteerdId) ?? null;
  const teAccepteren = afspraken.filter((r) => r.status === 'GEPLAND' && r.bevestiging_status === 'WACHT' && r.voorgesteld_door === 'employer');

  const acties = (a: ProfessionalB2bAfspraak) => {
    const begonnen = new Date(a.start_tijd) <= new Date();
    if (a.status === 'GEPLAND' && a.bevestiging_status === 'WACHT') {
      return a.voorgesteld_door === 'employer' ? (
        <>
          <Button size="sm" onClick={() => bevestig.mutate(a.id)} loading={bevestig.isPending}><Check className="w-4 h-4 mr-1" />Accepteren</Button>
          <Button size="sm" variant="outline" onClick={() => weiger.mutate(a.id)}><X className="w-4 h-4 mr-1" />Weigeren</Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => trekIn.mutate(a.id)}><Ban className="w-4 h-4 mr-1" />Intrekken</Button>
      );
    }
    if (['GEPLAND', 'AFGEWEZEN'].includes(a.status) && a.bevestiging_status === 'BEVESTIGD') {
      return (
        <Button
          size="sm"
          disabled={!begonnen}
          title={begonnen ? undefined : 'Uren indienen kan pas na aanvang van de afspraak'}
          onClick={() => {
            const invoer = window.prompt(`Gewerkte uren (gepland: ${a.uren_gepland})`, String(a.uren_gepland));
            const uren = invoer ? Number(invoer.replace(',', '.')) : NaN;
            if (uren > 0) dienIn.mutate({ id: a.id, uren });
          }}
        >
          <Send className="w-4 h-4 mr-1" />Uren indienen
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/professional/agenda" className="inline-flex items-center text-sm text-blue-600 hover:underline mb-1"><ArrowLeft className="w-4 h-4 mr-1" />Mijn Agenda</Link>
          <h1 className="text-2xl font-bold text-gray-900">Afspraken met opdrachtgevers</h1>
          <p className="text-sm text-gray-600">Meetings, telefonisch overleg en overleg op locatie. Alleen wederzijds bevestigde afspraken zijn declarabel.</p>
        </div>
        <Button onClick={() => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); setNieuwOp(d); }}>
          <Plus className="w-4 h-4 mr-1" />Nieuwe afspraak
        </Button>
      </div>

      {teAccepteren.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-amber-900">Uitnodigingen van opdrachtgevers — accepteer of weiger</p>
          {teAccepteren.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-md border border-amber-200 px-3 py-2 text-sm">
              <button type="button" className="text-left hover:underline" onClick={() => setGeselecteerdId(a.id)}>
                <span className="font-medium">{a.titel}</span> · {a.organisatie} · {new Date(a.start_tijd).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })} ({a.uren_gepland} u)
              </button>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => bevestig.mutate(a.id)}>Accepteren</Button>
                <Button size="sm" variant="outline" onClick={() => weiger.mutate(a.id)}>Weigeren</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-600 py-12 text-center">Laden…</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
          <B2bAgenda
            afspraken={afspraken}
            ondertitel={(a) => (a as ProfessionalB2bAfspraak).organisatie}
            onAfspraakClick={(a) => setGeselecteerdId(a.id)}
            onSlotClick={setNieuwOp}
            geselecteerdId={geselecteerdId}
          />
          {geselecteerd ? (
            <B2bAfspraakDetail afspraak={geselecteerd} tegenpartij={geselecteerd.organisatie} kijker="professional" onSluiten={() => setGeselecteerdId(null)}>
              {acties(geselecteerd)}
            </B2bAfspraakDetail>
          ) : (
            <div className="bg-white rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              Klik op een afspraak voor details, of op een leeg tijdvak om een afspraak in te plannen.
            </div>
          )}
        </div>
      )}

      {nieuwOp && (
        <B2bAfspraakFormulier
          start={nieuwOp}
          tegenpartijLabel="Opdrachtgever"
          tegenpartijen={werkgevers.map((w) => ({ id: w.employer_id, label: w.organisatie }))}
          bevestigHint="De opdrachtgever moet deze afspraak bevestigen. Zonder bevestiging worden de uren niet meegenomen in de maandelijkse factuur."
          bezig={plan.isPending}
          onSluiten={() => setNieuwOp(null)}
          onOpslaan={({ tegenpartij_id, ...rest }) => plan.mutate({ employer_id: tegenpartij_id, ...rest })}
        />
      )}
    </div>
  );
};

export default ProfessionalB2bAgendaPage;
